import React from 'react';
import _ from 'lodash';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import moment from 'moment';
import { renderToString } from 'react-dom/server';
import styled from 'styled-components';
import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import { Flex } from '../base/layout-components';
import BoldedValue from '../BoldedValue';
import TooltipWrapper from '../Tooltips/TooltipWrapper';
import { getColor } from 'common/constants/_colors';
import Text from '../base/Text';
import { SPACES } from 'common/constants/sizes';

const Legend = styled.div`
  width: 1.5rem;
  height: ${({ isLine }) => (isLine ? '3px' : '1rem')};
  border-radius: 10px;
  background-color: ${({ color }) => color};
`;

const LegendItem = ({ color, name, isLine }) => (
  <Flex items='center'>
    <Legend isLine={isLine} color={color} />
    <Text pl={SPACES.small} color='abbey' size='small'>
      {name}
    </Text>
  </Flex>
);

const Tooltip = ({ title, data, labels, formatter }) => {
  return (
    <TooltipWrapper title={title}>
      <Flex direction='column' padding='small'>
        {data.map((val, index) => (
          <BoldedValue
            key={title + index}
            value={{ text: formatter ? formatter(val) : val, size: 'x-large' }}
            description={{ text: labels[index], size: 'small' }}
          />
        ))}
      </Flex>
    </TooltipWrapper>
  );
};

const BarChart = ({
  tooltipLabels,
  data = {},
  withLineChart,
  tickInterval = 2,
  yProps = {},
  xProps = {},
  tooltipProps = {},
  barColor = 'chambray',
  barWidth,
  backgroundColor = getColor('white'),
  smallGraph = false,
  showTooltip = true,
  rotation = null,
  margin,
  onPointClick = null
}) => {
  const reduceGraphSize = Object.keys(data).length > 14 || smallGraph;
  const chartOptions = {
    plotOptions: {
      series: {
        animation: false,
        cursor: !!onPointClick ? 'pointer' : 'default',
        point: {
          events: {
            click: onPointClick
          }
        }
      }
    },
    chart: {
      style: { fontFamily: 'Outfit' },
      backgroundColor,
      ...(margin && { margin })
    },
    title: {
      text: ''
    },
    xAxis: {
      categories:
        xProps && xProps.categories
          ? xProps.categories
          : Object.keys(data).map(key => moment(key).toDate()),
      lineWidth: _.isNumber(_.get(xProps, 'lineWidth', null))
        ? xProps.lineWidth
        : 1,
      gridLineWidth: _.isNumber(_.get(xProps, 'gridLineWidth', null))
        ? xProps.gridLineWidth
        : 1,
      labels: {
        ...(_.get(xProps, 'labelDisabled', null)
          ? {
              enabled: false
            }
          : {}),
        rotation: rotation ? rotation : 0,
        style: {
          fontSize: '10px'
        },
        formatter() {
          if (xProps && xProps.formatter) {
            return xProps.formatter(this.value);
          }
          const weekDay = moment(this.value).weekday();
          if (reduceGraphSize && weekDay % 2 === 0) {
            return '';
          }
          return moment(this.value).format(
            weekDay === 1 ? 'dd<br>MMM D' : 'dd'
          );
        },
        overflow: 'allow'
      }
    },
    yAxis: [
      {
        min: 0,
        tickInterval,
        ...(yProps.tickAmount === 'default'
          ? {}
          : { tickAmount: _.get(yProps, 'tickAmount', 5) }),
        ...(yProps.softMax === 'default'
          ? {}
          : { softMax: _.get(yProps, 'softMax', 8) }),
        lineWidth: _.isNumber(_.get(yProps, 'lineWidth', null))
          ? yProps.lineWidth
          : 1,
        gridLineWidth: _.isNumber(_.get(yProps, 'gridLineWidth', null))
          ? yProps.gridLineWidth
          : 1,
        labels: {
          ...(_.get(yProps, 'labelDisabled', null)
            ? {
                enabled: false
              }
            : {}),
          ...(yProps && yProps.formatter
            ? {
                formatter() {
                  return yProps.formatter(this.value);
                }
              }
            : {}),
          ...(yProps && yProps.style
            ? {
                style: yProps.style
              }
            : {})
        },
        title:
          yProps && yProps.titleProps
            ? yProps.titleProps
            : {
                useHTML: true,
                text: withLineChart
                  ? renderToString(
                      <LegendItem
                        name={tooltipLabels[0]}
                        color={getColor('chambray')}
                      />
                    )
                  : tooltipLabels[0]
              }
      },
      ...(withLineChart
        ? [
            {
              min: 0,
              opposite: true,
              tickInterval,
              tickAmount: 5,
              title: {
                useHTML: true,
                rotation: '-90',
                text: renderToString(
                  <LegendItem
                    isLine
                    name={tooltipLabels[1]}
                    color={getColor('melrose')}
                  />
                )
              }
            }
          ]
        : [])
    ],

    legend: {
      enabled: false
    },
    tooltip: {
      enabled: showTooltip,
      shared: true,
      useHTML: true,
      backgroundColor: 'none',
      borderWidth: 0,
      shadow: false,
      padding: 0,
      ...(tooltipProps.outside && {
        outside: true
      }),
      ...(tooltipProps && tooltipProps.style),
      formatter() {
        if (tooltipProps && tooltipProps.formatter) {
          const currentRawData = _.get(data[this.x.before], '[0].rawData');
          const currentPercentage = _.get(
            data[this.x.before],
            '[0].percentage'
          );
          return tooltipProps.formatter(
            this.x,
            this.y,
            this.percentage,
            currentRawData,
            currentPercentage
          );
        }
        const momentDate = moment(this.x);
        const currentData = data[momentDate.format(SEARCH_DATE_FORMAT)];
        return renderToString(
          <Tooltip
            title={momentDate.format('dddd MMM D, YYYY')}
            data={currentData}
            labels={tooltipLabels}
            formatter={yProps.formatter}
          />
        );
      }
    },
    credits: {
      enabled: false
    },

    series: [
      {
        name: tooltipLabels[0],
        type: 'column',
        borderRadius: reduceGraphSize ? 4 : 8,
        pointWidth: barWidth ? barWidth : reduceGraphSize ? 7 : 14,
        color: getColor(barColor),
        groupPadding: 0.1,
        data: Object.values(data).map(value => value[0])
      },
      withLineChart
        ? {
            name: tooltipLabels[1],
            color: getColor('melrose'),
            yAxis: 1,
            marker: {
              radius: 0,
              lineWidth: 1
            },
            data: Object.values(data).map(value => value[1])
          }
        : {}
    ]
  };
  return (
    <HighchartsReact
      containerProps={{ style: { height: '100%', width: '100%' } }}
      highcharts={Highcharts}
      options={chartOptions}
    />
  );
};

export default BarChart;
